﻿import { Controller } from "@hotwired/stimulus";
import "toastr";

export default class extends Controller {
    static get values() {
        return {
            intercomSettings: Object,
            checkForUpToDateUserDetailsUrl: String,
            checkForModalNotificationsUrl: String,
            checkForPrizesUrl: String,
            checkForPopupQuizzesUrl: String,
            checkForToastNotificationsUrl: String,
            markNotificationAsReadUrl: String
        };
    }

    static get targets() {
        return ["headerMobileToggle", "helpCenterButton", "helpCenter"];
    }

    async connect() {
        this.setAppHeight();
        this.element.addEventListener("click", e => this.launchHelp(e));
        await this.checkForUpToDateUserDetails();
        await this.checkForModalNotifications();
        await this.checkForPrizes();
        await this.checkForPopupQuizzes();
        await this.checkForToastNotifications();
    }

    setAppHeight() {
        // document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    }

    toggleMenu(e) {
        this.headerMobileToggleTarget.classList.toggle("is-open");
        this.element.classList.toggle("menu-open");
        e.preventDefault();
    }

    closeMenu(e) {
        this.headerMobileToggleTarget.classList.remove("is-open");
        this.element.classList.remove("menu-open");
        e.preventDefault();
    }

    launchHelp(e) {
        if (e.target.tagName === "A" && (e.target.id === "launch-help" || e.target.classList.contains("launch-help"))) {
            bootstrap.Offcanvas.getOrCreateInstance(this.helpCenterTarget).show();
            e.preventDefault();
        }
    }

    async hideHelpCenter() {
        const hiddenPromise = new Promise(resolve => {
            this.helpCenterTarget.addEventListener(
                "hidden.bs.offcanvas",
                () => {
                    resolve();
                },
                { once: true }
            );
        });
        bootstrap.Offcanvas.getInstance(this.helpCenterTarget).hide();
        return hiddenPromise;
    }

    async initialiseIntercom() {
        return new Promise(resolve => {
            window.intercomSettings = this.intercomSettingsValue;
            const script = document.createElement("script");
            script.src = `https://widget.intercom.io/widget/${this.intercomSettingsValue.app_id}`;
            script.addEventListener("load", () => {
                resolve();
            });
            document.body.appendChild(script);
        });
    }

    async launchIntercom() {
        this.helpCenterButtonTarget.classList.add("d-none");
        const promises = [this.hideHelpCenter()];

        if (!window.Intercom) {
            promises.push(this.initialiseIntercom());
        }

        await Promise.allSettled(promises);
        window.Intercom("show");
        window.Intercom("onHide", () => {
            this.helpCenterButtonTarget.classList.remove("d-none");
            this.helpCenterButtonTarget.focus();
        });
    }

    async checkForAndShowModal(checkForUrl) {
        const preventKey = `layout:prevent-modal-with-check-for-url:${checkForUrl}`;

        if (sessionStorage[preventKey]) {
            return;
        }

        const response = await fetch(checkForUrl);
        const template = document.createElement("template");
        template.innerHTML = await response.text();

        if (!template.content.firstElementChild) {
            return;
        }

        const modalElement = this.element.appendChild(template.content.firstElementChild.cloneNode(true));
        htmx.process(modalElement);
        const modal = new bootstrap.Modal(modalElement);
        let isCancel;
        modalElement.querySelector("[data-is-cancel]")?.addEventListener(
            "click",
            _ => {
                isCancel = true;
                modal.hide();
            },
            { once: true }
        );
        const hiddenPromise = new Promise(r => {
            modalElement.addEventListener("hidden.bs.modal", r, { once: true });
        });
        modal.show();
        await hiddenPromise;

        if (isCancel) {
            sessionStorage[preventKey] = true;
        }

        return !isCancel;
    }

    async checkForUpToDateUserDetails() {
        return this.checkForAndShowModal(this.checkForUpToDateUserDetailsUrlValue);
    }

    async checkForModalNotifications() {
        while (await this.checkForAndShowModal(this.checkForModalNotificationsUrlValue)) {}
    }

    checkForPrizes() {
        return this.checkForAndShowModal(this.checkForPrizesUrlValue);
    }

    async checkForPopupQuizzes() {
        while (await this.checkForAndShowModal(this.checkForPopupQuizzesUrlValue)) {}
    }

    async markNotificationAsRead(id) {
        const body = new URLSearchParams();
        body.set("id", id);
        await fetch(
            this.markNotificationAsReadUrlValue,
            {
                method: "post",
                body: body
            }
        );
    }

    async checkForToastNotifications() {
        const response = await fetch(this.checkForToastNotificationsUrlValue);
        const notifications = await response.json();
        notifications.forEach(n => {
            let options = {
                onclick: () => this.markNotificationAsRead(n.id),
                onCloseClick: () => this.markNotificationAsRead(n.id)
            };

            if (n.requireDismissal) {
                options = {
                    ...options,
                    closeButton: true,
                    timeOut: 0,
                    extendedTimeOut: 0
                };
            }

            toastr[n.type](n.body, n.title, options);
        });
    }
}
